<template>
  <div class="remittance-page">
    <el-card shadow="never" class="remittance-header">
      <div class="remittance-header__content">
        <div class="remittance-header__title-block">
          <span class="remittance-header__eyebrow">Payroll Accounting</span>
          <h1 class="remittance-header__title">Manage Remittance</h1>
          <div class="remittance-header__meta">
            <el-tag size="small" type="success">{{ aStatusLabel || 'Payroll' }}</el-tag>
            <span class="remittance-header__campus">Campus {{ campus_id }}</span>
          </div>
        </div>
        <div class="remittance-header__stats">
          <div class="remittance-stat">
            <span class="remittance-stat__label">Available Ledgers</span>
            <strong>{{ report.length }}</strong>
          </div>
          <div class="remittance-stat">
            <span class="remittance-stat__label">Total Records</span>
            <strong>{{ pageTotal }}</strong>
          </div>
        </div>
      </div>
    </el-card>

    <el-card class="box-card remittance-card" shadow="never">
      <div slot="header" class="remittance-card__header">
        <div>
          <span class="remittance-card__title">Remittance Workbench</span>
          <p>{{ activeTab === 'single' ? 'Choose a deduction per ledger, then generate its remittance file.' : 'Choose an agency, then generate one consolidated file grouped by its deductions.' }}</p>
        </div>
        <el-tag size="mini" type="info">Page {{ meta.page || 1 }}</el-tag>
      </div>
      <el-tabs v-model="activeTab" class="remittance-tabs">
        <el-tab-pane label="Single Remittance" name="single">
          <single-remittance-tab
            :deduction="deduction"
            :loading="loading"
            :meta="meta"
            :month-list="monthList"
            :report="report"
            :selected-deductions="list"
            :sel="sel"
            @download="download"
            @page-change="getAll"
            @set-deduction="setDeduction"
            @update-page="updatePage"/>
        </el-tab-pane>

        <el-tab-pane label="Consolidated Remittance" name="consolidated">
          <consolidated-remittance-tab
            :agency-loading="agencyLoading"
            :agency-options="agencyOptions"
            :deduction-loading="deductionLoading"
            :loading="loading"
            :meta="meta"
            :month-list="monthList"
            :report="report"
            :selected-agency="selectedAgency"
            :selected-agency-deductions="selectedAgencyDeductions"
            :selected-agency-details="selectedAgencyDetails"
            @download-consolidated="downloadConsolidated"
            @page-change="getAll"
            @update-agency="updateAgency"
            @update-page="updatePage"/>
        </el-tab-pane>
      </el-tabs>
    </el-card>
  </div>
</template>

<script>
import { Agency } from '@/api/payroll/agency'
import { Deduction } from '@/api/payroll/deduction'
import { SummaryLedger } from '@/api/payroll/summaryledger'
import { getToken } from '@/utils/auth'
import ConsolidatedRemittanceTab from './ConsolidatedRemittanceTab'
import SingleRemittanceTab from './SingleRemittanceTab'
// import { NotifySuccess } from '../../../helper/Helper'

const GOVERNMENT_SHARE_DEDUCTION_IDS = [23, 34, 36]
const MONTH_LIST = [
  'JANUARY',
  'FEBRUARY',
  'MARCH',
  'APRIL',
  'MAY',
  'JUNE',
  'JULY',
  'AUGUST',
  'SEPTEMBER',
  'OCTOBER',
  'NOVEMBER',
  'DECEMBER'
]

export default {
  name: 'PayrollReport',
  components: {
    ConsolidatedRemittanceTab,
    SingleRemittanceTab
  },
  data() {
    return {
      meta: {
        page: 1
      },
      astatus: parseInt(this.$store.getters.APStatus) === 3 ? 'Permanent' : 'casCon',
      deduction: {},
      list: {},
      sel: 0,
      activeTab: 'single',
      selectedAgency: null,
      agencyList: [],
      agencyLoading: false,
      agencyDeductionList: [],
      agencyDeductionCache: {},
      deductionLoading: false,
      governmentShareDeductionIds: GOVERNMENT_SHARE_DEDUCTION_IDS,
      url: '',
      base_url: this.$store.getters.base_url,
      report: [],
      loading: false,
      monthList: MONTH_LIST,
      tenure: parseInt(this.$store.getters.APStatus),
      aStatusLabel: null,
      campus_id: this.$store.getters.user.currentUser.personnel.department.campus_id
    }
  },
  computed: {
    pageTotal() {
      return this.meta && this.meta.total ? this.meta.total : this.report.length
    },
    agencyOptions() {
      return this.agencyList.map(agency => {
        return {
          id: agency.id,
          shortName: agency.name,
          name: agency.name,
          address: agency.address,
          description: 'Groups all deductions assigned to this agency for the selected payroll month.',
          includeText: 'All deductions assigned to this agency',
          tags: ['Agency ID ' + agency.id],
          deductions: []
        }
      })
    },
    selectedAgencyDetails() {
      return this.agencyOptions.find(agency => agency.id === this.selectedAgency) || {
        id: null,
        shortName: 'Select agency',
        name: 'Select agency',
        description: 'Choose an agency to view its deductions.',
        tags: [],
        deductions: []
      }
    },
    selectedAgencyDeductions() {
      return this.agencyDeductionList
    },
    selectedAgencyDeductionIds() {
      return this.selectedAgencyDeductions.map(item => item.id).filter(id => id)
    }
  },
  watch: {
    selectedAgency: function() {
      this.getAgencyDeductions()
    }
  },
  created() {
    this.loading = true
    this.$store.dispatch('APStatus', this.$store.getters.APStatus)
    if (this.tenure === 3) { this.aStatusLabel = 'Permanent' }
    if (this.tenure === 2) { this.aStatusLabel = 'Contractual' }
    if (this.tenure === 1) { this.aStatusLabel = 'Casual' }
    this.getAgencies()
    this.getAll()
  },
  methods: {
    getAgencies() {
      this.agencyLoading = true
      Agency({ term: '', per_page: 999 }, 'get', '').then(res => {
        this.agencyList = res.data.agencies || []
        if ((!this.selectedAgency || !this.agencyOptions.find(agency => agency.id === this.selectedAgency)) && this.agencyOptions.length) {
          this.selectedAgency = this.agencyOptions[0].id
        }
        this.agencyLoading = false
      }).catch(error => {
        console.log(error)
        this.agencyLoading = false
      })
    },
    getAgencyDeductions() {
      if (!this.selectedAgency) {
        this.agencyDeductionList = []
        return
      }
      if (this.agencyDeductionCache[this.selectedAgency]) {
        this.agencyDeductionList = this.agencyDeductionCache[this.selectedAgency]
        this.deductionLoading = false
        return
      }
      const agencyId = this.selectedAgency
      this.deductionLoading = true
      Deduction({ term: '', per_page: 999, agency_id: agencyId }, 'get', '').then(res => {
        if (parseInt(this.selectedAgency) !== parseInt(agencyId)) return
        const deductions = res.data.deduction || []
        const agencyDeductions = deductions
          .filter(item => parseInt(item.agency_id) === parseInt(this.selectedAgency))
          .map(item => this.formatAgencyDeduction(item))
        this.$set(this.agencyDeductionCache, agencyId, agencyDeductions)
        this.agencyDeductionList = agencyDeductions
        this.deductionLoading = false
      }).catch(error => {
        if (parseInt(this.selectedAgency) !== parseInt(agencyId)) return
        console.log(error)
        this.agencyDeductionList = []
        this.deductionLoading = false
      })
    },
    getAll() {
      this.loading = true
      SummaryLedger({ report: true, page: this.meta.page, remittance: true, tenure: this.tenure }, 'get', '').then(res => {
        this.report = res.data.summary_ledger
        this.meta = res.data.meta
        this.loading = false
      }).catch(error => {
        console.log(error)
        this.loading = false
      })
    },
    setDeduction(id, row) {
      if (id) {
        this.$set(this.list, row, id)
      } else {
        this.$delete(this.list, row)
      }
    },
    hasSelectedDeduction(index) {
      return !!this.list[index]
    },
    formatAgencyDeduction(deduction) {
      return {
        id: deduction.id,
        code: deduction.code,
        description: deduction.desc,
        share: this.isGovernmentShareDeduction(deduction.id) ? 'Employee + Government' : 'Employee only'
      }
    },
    isGovernmentShareDeduction(id) {
      return this.governmentShareDeductionIds.indexOf(parseInt(id)) !== -1
    },
    updateAgency(id) {
      this.selectedAgency = id
    },
    updatePage(page) {
      this.meta.page = page
    },
    download(type, option, row, index) {
      if (!this.hasSelectedDeduction(index)) {
        this.$message.warning('Please select a deduction before generating remittance.')
        return
      }
      this.loading = true
      this.url = this.base_url + '/payroll/remittance?month=' + row.month + '&astatus=' + this.astatus + '&status = true' + '&year=' + row.year + '&deduction=' + this.list[index] + '&month=' + row.month + '&option=' + option + '&tenure=' + this.tenure + '&campus_id=' + this.campus_id + '&type=' + type + '&token=' + getToken() + '&dsk=' + row.id
      window.open(this.url, '_blank')
      this.loading = false
    },
    downloadConsolidated(row) {
      this.loading = true
      const agency = this.selectedAgencyDetails
      const query = [
        'month=' + row.month,
        'astatus=' + this.astatus,
        'status=true',
        'year=' + row.year,
        'agency_id=' + agency.id,
        'agency=' + encodeURIComponent(agency.shortName),
        'option=first',
        'tenure=' + this.tenure,
        'campus_id=' + this.campus_id,
        'type=payroll',
        'token=' + getToken(),
        'dsk=' + row.id
      ]
      if (this.selectedAgencyDeductionIds.length) {
        query.push('deduction_ids=' + this.selectedAgencyDeductionIds.join(','))
      }
      this.url = this.base_url + '/payroll/remittance/consolidated?' + query.join('&')
      window.open(this.url, '_blank')
      this.loading = false
    }
  }
}
</script>

<style lang="scss">
.remittance-page {
  padding: 10px;
}

.remittance-header {
  margin: 5px;
  border: 0;
  border-left: 5px solid #28a73a;
}

.remittance-header__content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  min-height: 94px;
}

.remittance-header__eyebrow,
.remittance-stat__label {
  color: #7c8798;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.remittance-header__title {
  margin: 6px 0 8px;
  color: #25313f;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.2;
}

.remittance-header__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.remittance-header__campus {
  color: #596575;
  font-size: 13px;
}

.remittance-header__stats {
  display: grid;
  grid-template-columns: repeat(2, minmax(130px, 1fr));
  gap: 10px;
}

.remittance-stat {
  min-height: 72px;
  padding: 14px 16px;
  border: 1px solid #e5e9f0;
  border-radius: 6px;
  background: #f8fafc;
}

.remittance-stat strong {
  display: block;
  margin-top: 8px;
  color: #1f2d3d;
  font-size: 24px;
  line-height: 1;
}

.remittance-card {
  border: 1px solid #e4e7ed;
}

.remittance-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.remittance-card__title {
  display: block;
  color: #1f2d3d;
  font-size: 16px;
  font-weight: 700;
}

.remittance-card__header p {
  margin: 4px 0 0;
  color: #7c8798;
  font-size: 13px;
  line-height: 1.4;
}

.remittance-tabs {
  margin-top: -6px;
}

.remittance-table-wrap {
  overflow-x: auto;
}

.remittance-table {
  min-width: 720px;
}

.month-name {
  color: #25313f;
  font-weight: 700;
}

.deduction-field {
  width: 100%;
}

.deduction-field >>> .el-select {
  width: 100%;
}

.generate-button {
  min-width: 112px;
}

.generate-button--wide {
  min-width: 132px;
}

.generate-button i {
  margin-right: 6px;
}

.remittance-pagination {
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
}

.consolidated-panel {
  display: grid;
  grid-template-columns: minmax(260px, 320px) minmax(0, 1fr);
  gap: 16px;
}

.agency-selector {
  align-self: start;
  padding: 16px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  background: #fbfcfe;
}

.agency-selector__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.agency-selector__title {
  color: #1f2d3d;
  font-size: 15px;
  font-weight: 700;
}

.agency-select {
  width: 100%;
}

.agency-details {
  display: flex;
  gap: 12px;
  margin-top: 16px;
  padding: 14px;
  border: 1px solid #dfe7d7;
  border-radius: 6px;
  background: #f6fbf2;
}

.agency-details__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  color: #1f7a2e;
  background: #e2f4db;
  font-size: 18px;
}

.agency-details__name {
  display: block;
  color: #25313f;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
}

.agency-details p {
  margin: 5px 0 0;
  color: #596575;
  font-size: 13px;
  line-height: 1.4;
}

.agency-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
}

.consolidated-table {
  overflow-x: auto;
}

.generate-list {
  min-width: 720px;
  margin-bottom: 16px;
  padding: 14px;
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  background: #ffffff;
}

.generate-list__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.generate-list__title {
  display: block;
  color: #1f2d3d;
  font-size: 15px;
  font-weight: 700;
}

.generate-list__header p {
  margin: 4px 0 0;
  color: #7c8798;
  font-size: 13px;
  line-height: 1.4;
}

.generate-list__empty-note {
  margin: 10px 0 0;
  color: #7c8798;
  font-size: 12px;
  line-height: 1.4;
}

.deduction-preview-table {
  min-width: 640px;
}

.agency-cell {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.agency-cell strong {
  color: #25313f;
}

.agency-cell span,
.include-text {
  color: #7c8798;
  font-size: 12px;
  line-height: 1.4;
}

@media (max-width: 768px) {
  .remittance-page {
    padding: 6px;
  }

  .remittance-header__content,
  .remittance-card__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .remittance-header__title {
    font-size: 24px;
  }

  .remittance-header__stats {
    width: 100%;
    grid-template-columns: 1fr;
  }

  .remittance-pagination {
    justify-content: center;
  }

  .consolidated-panel {
    grid-template-columns: 1fr;
  }

  .generate-list {
    min-width: 0;
  }
}

</style>
